/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { useMutation } from '@tanstack/react-query';

import { UnarchiveNodeConfigResponse, ApiError } from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

export type UnarchiveNodeConfigRequest = {
  configId: string;
};

export type UnarchiveNodeConfigMutationConfig = {
  onSuccess?: (data: UnarchiveNodeConfigResponse, variables: UnarchiveNodeConfigRequest) => void;
  onError: (error: unknown, variables: UnarchiveNodeConfigRequest) => void;
  onSettled?: (data: unknown, error: unknown, variables: unknown) => void;
  onMutate?: (variables: UnarchiveNodeConfigRequest) => void;
};

/**
 * Hook for archiving a node configuration through the management plane API.
 *
 * @param config.configId - ID of the configuration to unarchive
 * @param config.onSuccess - Called after successful configuration unarchive
 * @param config.onError - Called if unarchive fails
 * @param config.onSettled - Called after mutation completes (success or failure)
 *
 * @returns React Query mutation result for archiving node configuration
 *
 * @example
 * ```tsx
 * const { mutate } = useUnarchiveNodeConfigMutation({
 *   configId: '456',
 *   onSuccess: (data) => console.log('Configuration Unarchived:', data),
 *   onError: (error) => console.error('Unarchive failed:', error)
 * });
 *
 * // Unarchive configuration
 * mutate({ configId: '456' });
 * ```
 */
export function useUnarchiveNodeConfigMutation({ onSuccess, onError, onSettled }: UnarchiveNodeConfigMutationConfig) {
  const tenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);

  const { mutate, isError, error } = useMutation<UnarchiveNodeConfigResponse, ApiError, UnarchiveNodeConfigRequest>({
    mutationFn: ({ configId }) => {
      return mgmtPlaneAPIClient.nodeConfigs.unarchiveNodeConfig({
        tenantId: tenantId!,
        configId,
      });
    },
    onSuccess,
    onError,
    onSettled,
  });

  return { mutate, isError, error };
}
